<template>
  <div class="blog-content">
    <div class="blog-wrapper">
      <BlogToolbar>
        <template #left-block>
          <IRBaseButton
            :href="aboutLink"
            class="logo-block"
            plain
            nuxt
            large
            aria-label="back to the app"
            target="_blank"
          >
            <BaseSpriteIcon
              custom-class="app-logo-icon"
              :icon-name="`ico-logo-${logoName}`"/>
            <BaseSpriteIcon
              custom-class="app-logo-text"
              :viewBox="viewBoxSize"
              :icon-name="`pic-app-name-${logoName}`"/></IRBaseButton
        ></template>
        <template #middle-block>
          <div class="controls-block">
            <IRBaseButton
              v-for="item in linkItems"
              :key="item.label"
              :href="item.href"
              nuxt
              style="padding: 0 12px;"
              :target="item.target"
            >
              {{ item.label }}
            </IRBaseButton>
          </div>
        </template>
        <template #right-block>
          <div class="controls-block">
            <IRBaseButton
              v-for="item in iconLinkItems"
              :key="item.label"
              :href="item.href"
              :target="item.target"
              fab
              nuxt
              outlined
              small
            >
              <BaseSpriteIcon :icon-name="item.icon"></BaseSpriteIcon>
            </IRBaseButton>
          </div>
        </template>
      </BlogToolbar>
      <div class="blog-title mt-14">
        <h2>Articles</h2>
      </div>
      <div class="blog-top-controls mt-3">
        <BlogArticlesFilterByTags
          :tags="articlesTags"
          :tags-selected="preselectedTags"
          @onChangeTagFilter="changeTagFilterHandler"
        />
      </div>

      <div class="search-block mt-1">
        <IRBaseTextField
          v-model="searchString"
          type="text"
          outlined
          dense
          append-icon="ico-close"
          placeholder="Find article..."
          :disabled="!isOnline"
          @input="debouncedOnInput"
          @click:append="clearInput"
        />
      </div>

      <div ref="articlesBlock" class="articles-block mt-4">
        <div
          v-for="article in articles"
          :key="article.articleId"
          class="article-item"
        >
          <div class="item-pic-block">
            <nuxt-img
              :src="article.coverUrl"
              :alt="article.title"
              sizes="xs:288px sm:608px md:720px lg:600px xl:448px"
            />
          </div>
          <div class="item-stats-block mt-3">
            <div>{{ article.publishScheduledDate }}</div>
            <div>{{ article.author }}</div>
            <div>
              <TimeDuration :duration="article.readingTime" dir="auto" /> read
            </div>
          </div>
          <div class="item-title-block mt-2">
            <h3>
              <NuxtLink
                :to="toArticle(article.slug)"
                class="item-title-block mt-2"
              >
                {{ article.title }}
              </NuxtLink>
            </h3>
          </div>
          <div class="item-text-block mt-2 mb-3">
            <p>
              {{ article.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <BlogFooter />
  </div>
</template>

<script>
import TimeDuration from '@/components/base/TimeDuration/TimeDuration';
import BlogToolbar from '@/components/views/BlogToolbar/BlogToolbar.vue';
import BlogFooter from '@/components/views/BlogFooter/BlogFooter.vue';
import BlogArticlesFilterByTags from '@/components/views/BlogArticlesFilterByTags/BlogArticlesFilterByTags.vue';
import IRBaseTextField from '@/components/base/IRBaseTextField/IRBaseTextField.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';
import IRBaseButton from '@/components/base/IRBaseButton/IRBaseButton.vue';

import BrandsEnum from '@shared/enums/BrandsEnum.mjs';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import BlogEvents from '@/enums/BlogEventsEnum';
import AppStateEnum from '@/enums/AppStateEnum';

export default {
  name: 'Blog',
  components: {
    BlogToolbar,
    BlogFooter,
    TimeDuration,
    BlogArticlesFilterByTags,
    IRBaseTextField,
    BaseSpriteIcon,
    IRBaseButton
  },
  data() {
    return {
      isActive: true,
      searchString: '',
      pageCount: 0,
      debouncedOnInput: debounce(this.onInput, 500),
      debouncedHandleScroll: debounce(this.handleScroll.bind(this), 200),
      preselectedTags: []
    };
  },
  computed: {
    ...mapGetters({
      articles: 'BlogStore/getArticles',
      articlesTags: 'BlogStore/getArticlesTags',
      isOnline: 'ContextStore/isOnline',
      isLoadMoreEnabled: 'BlogStore/isLoadMoreEnabled',
      articlesSelectedTags: 'BlogStore/getArticlesSelectedTags',
      clientReaderUrl: 'ContextStore/getClientReaderUrl',
      brand: 'ContextStore/brand'
    }),
    aboutLink() {
      return `${this.clientReaderUrl}about/`;
    },
    libraryLink() {
      return `${this.clientReaderUrl}library/`;
    },
    compilationLink() {
      return `${this.clientReaderUrl}extracts/`;
    },
    searchLink() {
      return `${this.clientReaderUrl}search/`;
    },
    isOcean() {
      return this.brand === BrandsEnum.OCEAN;
    },
    logoName() {
      return this.isOcean ? 'ocean' : 'ffa';
    },
    viewBoxSize() {
      return this.isOcean ? '0 0 156 26' : '0 0 160 21';
    },
    linkItems() {
      const items = [
        {
          label: 'Library',
          href: this.libraryLink,
          icon: 'ico-library-outline',
          target: '_blank'
        }
      ];
      if (this.isOcean) {
        items.push({
          label: 'Compilations',
          href: this.compilationLink,
          icon: 'ico-extract',
          target: '_blank'
        });
        items.push({
          label: 'Search',
          href: this.searchLink,
          target: '_blank'
        });
      } else {
        items.push({
          label: 'About',
          href: this.aboutLink,
          icon: 'ico-info',
          target: '_blank'
        });
      }
      return items;
    },
    iconLinkItems() {
      return this.linkItems.filter(i => !!i.icon);
    }
  },
  async mounted() {
    const preselectedTags = this.$route.params?.tags;
    this.preselectedTags = preselectedTags || this.articlesSelectedTags;

    this.addEventListener();
    if (preselectedTags?.length) {
      this.setArticlesSelectedTags(preselectedTags);
      await this.searchArticles();
    } else {
      this.handleScroll();
    }
  },
  destroyed() {
    this.removeEventListener();
  },
  methods: {
    onInput() {
      this.resetPageCount();
      this.searchArticles();
    },
    clearInput() {
      this.searchString = '';
      this.onInput();
    },
    changeTagFilterHandler(event) {
      switch (event.type) {
        case BlogEvents.FILTER_BY_TAG: {
          const tags = event.data;
          this.setArticlesSelectedTags(tags);
          this.resetPageCount();
          this.searchArticles();
          break;
        }
        case BlogEvents.RESET_SELECTED_TAGS: {
          this.setArticlesSelectedTags([]);
          this.resetPageCount();
          this.searchArticles();
          break;
        }
      }
    },
    setArticlesSelectedTags(tags) {
      this.$store.commit('BlogStore/setArticlesSelectedTags', tags);
    },
    searchArticles() {
      return this.$store.dispatch('BlogStore/searchArticles', {
        searchString: this.searchString,
        pageCount: this.pageCount
      });
    },
    toArticle(slug) {
      return {
        name: AppStateEnum.BLOG_ARTICLE,
        params: { articleSlug: slug }
      };
    },
    showMoreArticles() {
      this.increasePageCount();
      this.searchArticles();
    },
    increasePageCount() {
      this.pageCount++;
    },
    resetPageCount() {
      this.pageCount = 0;
    },
    addEventListener() {
      document.addEventListener('scroll', this.debouncedHandleScroll);
    },
    removeEventListener() {
      document.removeEventListener('scroll', this.debouncedHandleScroll);
    },
    handleScroll() {
      if (!this.isLoadMoreEnabled) {
        return;
      }
      const articlesBlockRect = this.$refs.articlesBlock.getBoundingClientRect();
      const firstArticleItem = this.$el.querySelector('.article-item');
      if (
        window.innerHeight >
        articlesBlockRect.bottom - (firstArticleItem.offsetHeight + 50)
      ) {
        this.showMoreArticles();
      }
    }
  }
};
</script>

<style src="./Blog.less" lang="less"></style>
